<template>
  <div v-redirect-if-no-permission:COMPLAINTS>
    <v-card class="mb-2">
      <div class="fin-header">
        <div class="fin-toolbar">
          <span class="fin-title">{{ $t("complaintsSystem.pageTitle") }}</span>
          <v-spacer></v-spacer>
          <v-btn small icon flat @click="loadComplaints">
            <v-icon>refresh</v-icon>
          </v-btn>
        </div>
        <div class="fin-filters">
          <v-select
            class="fin-field"
            :items="statusOptions"
            item-text="text"
            item-value="value"
            v-model="filters.status"
            :label="$t('complaintsSystem.filters.status')"
            clearable
            hide-details
          ></v-select>
          <v-select
            class="fin-field"
            :items="categoryOptions"
            item-text="text"
            item-value="value"
            v-model="filters.category"
            :label="$t('complaintsSystem.filters.category')"
            clearable
            hide-details
          ></v-select>
          <v-select
            class="fin-field"
            :items="complainantTypeOptions"
            item-text="text"
            item-value="value"
            v-model="filters.complainant_type"
            :label="$t('complaintsSystem.filters.complainantType')"
            clearable
            hide-details
          ></v-select>
          <v-text-field
            class="fin-field"
            v-model="filters.from"
            :label="$t('complaintsSystem.filters.from')"
            type="date"
            hide-details
          ></v-text-field>
          <v-text-field
            class="fin-field"
            v-model="filters.to"
            :label="$t('complaintsSystem.filters.to')"
            type="date"
            hide-details
          ></v-text-field>
          <v-text-field
            class="fin-field"
            v-model="filters.booking_id"
            :label="$t('complaintsSystem.filters.bookingId')"
            type="number"
            clearable
            hide-details
          ></v-text-field>
          <v-text-field
            class="fin-field fin-field--wide"
            v-model="filters.search"
            :label="$t('complaintsSystem.filters.search')"
            clearable
            hide-details
            @keyup.enter="applyFilters"
          ></v-text-field>
          <v-btn small color="primary" @click="applyFilters">{{ $t("complaintsSystem.filters.apply") }}</v-btn>
          <v-btn small flat @click="resetFilters">{{ $t("complaintsSystem.filters.reset") }}</v-btn>
        </div>
      </div>
    </v-card>

    <v-data-table
      :headers="headers"
      :items="complaints"
      :loading="loading"
      :pagination.sync="pagination"
      :total-items="total"
      :rows-per-page-items="[10, 25, 50, 100]"
      class="elevation-1"
    >
      <template v-slot:items="props">
        <tr class="complaint-row" @click="openDetails(props.item)">
          <td class="text-xs-center">#{{ props.item.id }}</td>
          <td class="text-xs-center">{{ formatDate(props.item.created_at) }}</td>
          <td>
            <span>{{ complainantName(props.item) }}</span>
            <v-chip
              small
              :color="typeColor(props.item.complainant_type)"
              text-color="white"
              class="ms-2"
            >
              {{ typeLabel(props.item.complainant_type) }}
            </v-chip>
          </td>
          <td>{{ categoryLabel(props.item.category) }}</td>
          <td>{{ detailLabel(props.item.detail_code) }}</td>
          <td class="text-xs-center">
            <a
              v-if="props.item.booking_id"
              class="booking-link"
              @click.stop="goToBooking(props.item.booking_id)"
            >
              #{{ props.item.booking_id }}
            </a>
            <span v-else>-</span>
          </td>
          <td class="text-xs-center">
            <v-chip small :color="statusColor(props.item.status)" text-color="white">
              {{ statusLabel(props.item.status) }}
            </v-chip>
          </td>
        </tr>
      </template>
    </v-data-table>

    <v-dialog v-model="detailsDialog" max-width="820px" scrollable>
      <v-card>
        <v-card-title>
          <span class="headline">
            {{ details ? $t("complaintsSystem.detailsDialog.title", { id: details.id }) : "" }}
          </span>
          <v-chip
            v-if="details"
            small
            :color="statusColor(details.status)"
            text-color="white"
            class="ms-2"
          >
            {{ statusLabel(details.status) }}
          </v-chip>
          <v-spacer></v-spacer>
          <v-btn icon @click="detailsDialog = false"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <v-progress-linear v-if="detailsLoading" indeterminate></v-progress-linear>
          <template v-if="details">
            <v-layout wrap>
              <v-flex xs12 md6 class="pa-1">
                <div class="party-card">
                  <div class="party-title">{{ $t("complaintsSystem.detailsDialog.complainant") }}</div>
                  <div class="party-name">
                    {{ partyName(details.complainant) }}
                    <v-chip
                      v-if="details.complainant && details.complainant.type"
                      small
                      :color="typeColor(details.complainant.type)"
                      text-color="white"
                      class="ms-2"
                    >
                      {{ typeLabel(details.complainant.type) }}
                    </v-chip>
                  </div>
                  <div v-if="details.complainant && details.complainant.phone" class="party-phone">
                    {{ $t("complaintsSystem.detailsDialog.phone") }}:
                    <span dir="ltr">{{ details.complainant.phone }}</span>
                  </div>
                </div>
              </v-flex>
              <v-flex xs12 md6 class="pa-1" v-if="details.against">
                <div class="party-card">
                  <div class="party-title">{{ $t("complaintsSystem.detailsDialog.against") }}</div>
                  <div class="party-name">
                    {{ partyName(details.against) }}
                    <v-chip
                      v-if="details.against.type"
                      small
                      :color="typeColor(details.against.type)"
                      text-color="white"
                      class="ms-2"
                    >
                      {{ typeLabel(details.against.type) }}
                    </v-chip>
                  </div>
                  <div v-if="details.against.phone" class="party-phone">
                    {{ $t("complaintsSystem.detailsDialog.phone") }}:
                    <span dir="ltr">{{ details.against.phone }}</span>
                  </div>
                </div>
              </v-flex>
            </v-layout>

            <div class="detail-line">
              <strong>{{ $t("complaintsSystem.detailsDialog.booking") }}:</strong>
              <a
                v-if="detailsBookingId"
                class="booking-link ms-1"
                @click="goToBooking(detailsBookingId)"
              >
                #{{ detailsBookingId }}
              </a>
              <span v-else class="ms-1">-</span>
            </div>
            <div class="detail-line">
              <strong>{{ $t("complaintsSystem.detailsDialog.createdAt") }}:</strong>
              <span class="ms-1">{{ formatDateTime(details.created_at) }}</span>
            </div>
            <div class="detail-line" v-if="details.resolved_at">
              <strong>{{ $t("complaintsSystem.detailsDialog.resolvedAt") }}:</strong>
              <span class="ms-1">{{ formatDateTime(details.resolved_at) }}</span>
            </div>
            <div class="detail-line">
              <strong>{{ $t("complaintsSystem.detailsDialog.category") }}:</strong>
              <span class="ms-1">{{ categoryLabel(details.category) }}</span>
            </div>
            <div class="detail-line">
              <strong>{{ $t("complaintsSystem.detailsDialog.detail") }}:</strong>
              <span class="ms-1">{{ detailLabel(details.detail_code) }}</span>
            </div>
            <div class="detail-line">
              <strong>{{ $t("complaintsSystem.detailsDialog.description") }}:</strong>
              <span class="ms-1">{{ details.description || $t("complaintsSystem.detailsDialog.noDescription") }}</span>
            </div>

            <div class="section-title">{{ $t("complaintsSystem.detailsDialog.attachments") }}</div>
            <div v-if="!(details.attachments && details.attachments.length)" class="empty-note">
              {{ $t("complaintsSystem.detailsDialog.noAttachments") }}
            </div>
            <div v-else class="attachments-grid">
              <div v-for="att in details.attachments" :key="'att-' + att.id" class="attachment-item">
                <template v-if="attachmentKind(att) === 'image'">
                  <v-progress-circular
                    v-if="media(att).loading"
                    indeterminate
                    size="24"
                  ></v-progress-circular>
                  <img
                    v-else-if="media(att).url"
                    :src="media(att).url"
                    class="attachment-media"
                    @click="openMedia(att)"
                  />
                  <a v-else class="booking-link" @click="fetchAttachment(att)">
                    {{ $t("complaintsSystem.detailsDialog.download") }}
                  </a>
                </template>
                <template v-else-if="attachmentKind(att) === 'video'">
                  <v-progress-circular
                    v-if="media(att).loading"
                    indeterminate
                    size="24"
                  ></v-progress-circular>
                  <video
                    v-else-if="media(att).url"
                    :src="media(att).url"
                    controls
                    class="attachment-media"
                  ></video>
                  <a v-else class="booking-link" @click="fetchAttachment(att)">
                    {{ $t("complaintsSystem.detailsDialog.download") }}
                  </a>
                </template>
                <template v-else>
                  <a class="booking-link" @click="downloadAttachment(att)">
                    {{ $t("complaintsSystem.detailsDialog.download") }}
                  </a>
                </template>
                <div class="attachment-meta">{{ att.mime || "" }} {{ formatSize(att.size) }}</div>
              </div>
            </div>

            <div class="section-title">{{ $t("complaintsSystem.detailsDialog.actionsHistory") }}</div>
            <div v-if="!(details.actions && details.actions.length)" class="empty-note">
              {{ $t("complaintsSystem.detailsDialog.noActions") }}
            </div>
            <v-data-table
              v-else
              :headers="actionHeaders"
              :items="details.actions"
              hide-actions
              class="elevation-1"
            >
              <template v-slot:items="props">
                <tr>
                  <td>{{ actionTypeLabel(props.item.action_type) }}</td>
                  <td class="text-xs-center">
                    {{ props.item.amount !== null && props.item.amount !== undefined ? formatMoney(props.item.amount) : "-" }}
                  </td>
                  <td>{{ props.item.reason }}</td>
                  <td>{{ actionAdminName(props.item) }}</td>
                  <td class="text-xs-center">{{ formatDateTime(props.item.created_at) }}</td>
                </tr>
              </template>
            </v-data-table>
          </template>
        </v-card-text>
        <v-card-actions v-if="details">
          <v-btn
            v-if="details.status === 'NEW'"
            color="orange"
            flat
            :loading="statusSaving"
            @click="setStatus('IN_REVIEW')"
          >
            {{ $t("complaintsSystem.detailsDialog.startReview") }}
          </v-btn>
          <v-btn
            v-if="details.status === 'NEW' || details.status === 'IN_REVIEW'"
            color="error"
            flat
            :disabled="statusSaving"
            @click="rejectDialog = true"
          >
            {{ $t("complaintsSystem.detailsDialog.reject") }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            v-if="details.status === 'NEW' || details.status === 'IN_REVIEW'"
            color="primary"
            @click="openActionDialog"
          >
            {{ $t("complaintsSystem.detailsDialog.takeAction") }}
          </v-btn>
          <v-btn flat @click="detailsDialog = false">
            {{ $t("complaintsSystem.detailsDialog.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="actionDialog" max-width="520px" persistent>
      <v-card>
        <v-card-title>
          {{ $t("complaintsSystem.actionModal.title") }}
          <v-spacer></v-spacer>
          <v-btn icon @click="closeActionDialog"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <v-select
            :items="actionTypeOptions"
            item-text="text"
            item-value="value"
            v-model="actionForm.action_type"
            :label="$t('complaintsSystem.actionModal.actionType')"
          ></v-select>
          <v-text-field
            v-if="isMoneyAction(actionForm.action_type)"
            v-model.number="actionForm.amount"
            :label="$t('complaintsSystem.actionModal.amount')"
            type="number"
            min="0"
          ></v-text-field>
          <v-textarea
            v-model="actionForm.reason"
            :label="$t('complaintsSystem.actionModal.reason')"
            rows="3"
            auto-grow
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="closeActionDialog" :disabled="actionSaving">
            {{ $t("complaintsSystem.actionModal.cancel") }}
          </v-btn>
          <v-btn color="primary" :loading="actionSaving" @click="submitAction">
            {{ $t("complaintsSystem.actionModal.submit") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="rejectDialog" max-width="420px" persistent>
      <v-card>
        <v-card-title>
          {{ $t("complaintsSystem.detailsDialog.rejectConfirmTitle") }}
        </v-card-title>
        <v-card-text>
          {{ details ? $t("complaintsSystem.detailsDialog.rejectConfirmText", { id: details.id }) : "" }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="rejectDialog = false" :disabled="statusSaving">
            {{ $t("complaintsSystem.actionModal.cancel") }}
          </v-btn>
          <v-btn color="error" :loading="statusSaving" @click="confirmReject">
            {{ $t("complaintsSystem.detailsDialog.reject") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { formatCompanyTime } from "@/util/companyTime";

const STATUSES = ["NEW", "IN_REVIEW", "RESOLVED", "REJECTED"];
const MONEY_ACTIONS = [
  "CAPTAIN_FINE",
  "CAPTAIN_COMPENSATION",
  "PASSENGER_FINE",
  "PASSENGER_COMPENSATION",
];
const ACTION_TYPES = MONEY_ACTIONS.concat(["WARNING", "CLOSE"]);

export default {
  name: "ComplaintsPage",
  title() {
    return this.$t("complaintsSystem.pageTitle");
  },
  data() {
    return {
      loading: false,
      complaints: [],
      total: 0,
      pagination: {
        page: 1,
        rowsPerPage: 25,
      },
      filters: this.emptyFilters(),
      detailsDialog: false,
      detailsLoading: false,
      details: null,
      attachmentMedia: {},
      statusSaving: false,
      rejectDialog: false,
      actionDialog: false,
      actionSaving: false,
      actionForm: this.emptyActionForm(),
    };
  },
  computed: {
    headers() {
      return [
        { text: this.$t("complaintsSystem.columns.id"), value: "id", align: "center", sortable: false },
        { text: this.$t("complaintsSystem.columns.createdAt"), value: "created_at", align: "center", sortable: false },
        { text: this.$t("complaintsSystem.columns.complainant"), value: "complainant", sortable: false },
        { text: this.$t("complaintsSystem.columns.category"), value: "category", sortable: false },
        { text: this.$t("complaintsSystem.columns.detail"), value: "detail_code", sortable: false },
        { text: this.$t("complaintsSystem.columns.booking"), value: "booking_id", align: "center", sortable: false },
        { text: this.$t("complaintsSystem.columns.status"), value: "status", align: "center", sortable: false },
      ];
    },
    actionHeaders() {
      return [
        { text: this.$t("complaintsSystem.detailsDialog.colType"), value: "action_type", sortable: false },
        { text: this.$t("complaintsSystem.detailsDialog.colAmount"), value: "amount", align: "center", sortable: false },
        { text: this.$t("complaintsSystem.detailsDialog.colReason"), value: "reason", sortable: false },
        { text: this.$t("complaintsSystem.detailsDialog.colAdmin"), value: "created_by", sortable: false },
        { text: this.$t("complaintsSystem.detailsDialog.colDate"), value: "created_at", align: "center", sortable: false },
      ];
    },
    statusOptions() {
      return STATUSES.map((s) => ({ text: this.statusLabel(s), value: s }));
    },
    categoryOptions() {
      return [1, 2, 3, 4].map((c) => ({ text: this.categoryLabel(c), value: c }));
    },
    complainantTypeOptions() {
      return ["PASSENGER", "CAPTAIN"].map((t) => ({ text: this.typeLabel(t), value: t }));
    },
    actionTypeOptions() {
      return ACTION_TYPES.map((t) => ({ text: this.actionTypeLabel(t), value: t }));
    },
    detailsBookingId() {
      if (!this.details) return null;
      if (this.details.booking && this.details.booking.id) return this.details.booking.id;
      return this.details.booking_id || null;
    },
  },
  watch: {
    pagination: {
      handler() {
        this.loadComplaints();
      },
      deep: true,
    },
    detailsDialog(open) {
      if (!open) this.cleanupDetails();
    },
  },
  mounted() {
    this.loadComplaints();
  },
  beforeDestroy() {
    this.revokeAttachmentUrls();
  },
  methods: {
    emptyFilters() {
      return {
        status: null,
        category: null,
        complainant_type: null,
        from: null,
        to: null,
        booking_id: null,
        search: null,
      };
    },
    emptyActionForm() {
      return {
        action_type: null,
        amount: null,
        reason: "",
      };
    },
    loadComplaints() {
      this.loading = true;
      const params = {
        page: this.pagination.page,
        perPage: this.pagination.rowsPerPage,
      };
      if (this.filters.status) params.status = this.filters.status;
      if (this.filters.category) params.category = this.filters.category;
      if (this.filters.complainant_type) params.complainant_type = this.filters.complainant_type;
      if (this.filters.from) params.from = this.filters.from;
      if (this.filters.to) params.to = this.filters.to;
      if (this.filters.booking_id) params.booking_id = this.filters.booking_id;
      if (this.filters.search) params.search = this.filters.search;
      this.axios
        .get(this.api.ACTIONS.COMPLAINTS, { params })
        .then((res) => {
          this.complaints = res.data.data.data || [];
          this.total = res.data.data.total || 0;
        })
        .catch((err) => {
          this.showError(err, "complaintsSystem.toasts.loadFailed");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    applyFilters() {
      if (this.pagination.page !== 1) this.pagination.page = 1;
      else this.loadComplaints();
    },
    resetFilters() {
      this.filters = this.emptyFilters();
      this.applyFilters();
    },
    openDetails(item) {
      this.detailsDialog = true;
      this.details = null;
      this.detailsLoading = true;
      this.axios
        .get(this.api.ACTIONS.COMPLAINT(item.id))
        .then((res) => {
          this.details = res.data.data;
          this.autoFetchAttachments();
        })
        .catch((err) => {
          this.showError(err, "complaintsSystem.toasts.detailsFailed");
          this.detailsDialog = false;
        })
        .finally(() => {
          this.detailsLoading = false;
        });
    },
    reloadDetails() {
      if (!this.details) return;
      this.axios
        .get(this.api.ACTIONS.COMPLAINT(this.details.id))
        .then((res) => {
          this.details = res.data.data;
        })
        .catch(() => {});
    },
    cleanupDetails() {
      this.revokeAttachmentUrls();
      this.attachmentMedia = {};
      this.details = null;
    },
    revokeAttachmentUrls() {
      Object.keys(this.attachmentMedia).forEach((id) => {
        const m = this.attachmentMedia[id];
        if (m && m.url) URL.revokeObjectURL(m.url);
      });
    },
    autoFetchAttachments() {
      if (!this.details) return;
      (this.details.attachments || []).forEach((att) => {
        const kind = this.attachmentKind(att);
        if (kind === "image" || kind === "video") this.fetchAttachment(att);
      });
    },
    media(att) {
      return this.attachmentMedia[att.id] || {};
    },
    attachmentKind(att) {
      const mime = att.mime || "";
      if (mime.indexOf("image/") === 0) return "image";
      if (mime.indexOf("video/") === 0) return "video";
      return "file";
    },
    fetchAttachment(att) {
      const existing = this.attachmentMedia[att.id];
      if (existing && (existing.loading || existing.url)) return;
      const complaintId = this.details.id;
      this.$set(this.attachmentMedia, att.id, { loading: true, url: null });
      this.axios
        .get(this.api.ACTIONS.COMPLAINT_ATTACHMENT(complaintId, att.id), {
          responseType: "blob",
        })
        .then((res) => {
          const url = URL.createObjectURL(res.data);
          this.$set(this.attachmentMedia, att.id, { loading: false, url });
        })
        .catch(() => {
          this.$set(this.attachmentMedia, att.id, { loading: false, url: null });
          window.getApp.$emit(
            "SHOW_TOAST",
            this.$t("complaintsSystem.detailsDialog.attachmentLoadFailed"),
            "red"
          );
        });
    },
    downloadAttachment(att) {
      this.axios
        .get(this.api.ACTIONS.COMPLAINT_ATTACHMENT(this.details.id, att.id), {
          responseType: "blob",
        })
        .then((res) => {
          const url = URL.createObjectURL(res.data);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `complaint-${this.details.id}-attachment-${att.id}`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        })
        .catch(() => {
          window.getApp.$emit(
            "SHOW_TOAST",
            this.$t("complaintsSystem.detailsDialog.attachmentLoadFailed"),
            "red"
          );
        });
    },
    openMedia(att) {
      const m = this.media(att);
      if (m.url) window.open(m.url, "_blank");
    },
    setStatus(status) {
      if (!this.details) return;
      this.statusSaving = true;
      this.axios
        .put(this.api.ACTIONS.COMPLAINT_STATUS(this.details.id), { status })
        .then(() => {
          window.getApp.$emit("SHOW_TOAST", this.$t("complaintsSystem.toasts.statusUpdated"), "green");
          this.reloadDetails();
          this.loadComplaints();
        })
        .catch((err) => {
          this.showError(err, "complaintsSystem.toasts.loadFailed");
        })
        .finally(() => {
          this.statusSaving = false;
        });
    },
    confirmReject() {
      if (!this.details) {
        this.rejectDialog = false;
        return;
      }
      const complaintId = this.details.id;
      this.statusSaving = true;
      this.axios
        .put(this.api.ACTIONS.COMPLAINT_STATUS(complaintId), { status: "REJECTED" })
        .then(() => {
          window.getApp.$emit("SHOW_TOAST", this.$t("complaintsSystem.toasts.statusUpdated"), "green");
          this.reloadDetails();
          this.loadComplaints();
        })
        .catch((err) => {
          this.showError(err, "complaintsSystem.toasts.loadFailed");
        })
        .finally(() => {
          this.statusSaving = false;
          this.rejectDialog = false;
        });
    },
    openActionDialog() {
      this.actionForm = this.emptyActionForm();
      this.actionDialog = true;
    },
    closeActionDialog() {
      this.actionDialog = false;
      this.actionForm = this.emptyActionForm();
    },
    isMoneyAction(type) {
      return MONEY_ACTIONS.indexOf(type) !== -1;
    },
    submitAction() {
      if (!this.details) return;
      const type = this.actionForm.action_type;
      if (!type) {
        window.getApp.$emit("SHOW_TOAST", this.$t("complaintsSystem.toasts.actionTypeRequired"), "red");
        return;
      }
      const reason = (this.actionForm.reason || "").trim();
      if (!reason) {
        window.getApp.$emit("SHOW_TOAST", this.$t("complaintsSystem.toasts.reasonRequired"), "red");
        return;
      }
      const payload = { action_type: type, reason };
      if (this.isMoneyAction(type)) {
        const amount = this.toStoredAmount(this.actionForm.amount);
        if (!amount || amount <= 0) {
          window.getApp.$emit("SHOW_TOAST", this.$t("complaintsSystem.toasts.amountRequired"), "red");
          return;
        }
        payload.amount = amount;
      }
      this.actionSaving = true;
      this.axios
        .post(this.api.ACTIONS.COMPLAINT_ACTION(this.details.id), payload)
        .then(() => {
          window.getApp.$emit("SHOW_TOAST", this.$t("complaintsSystem.toasts.actionSubmitted"), "green");
          this.closeActionDialog();
          this.reloadDetails();
          this.loadComplaints();
        })
        .catch((err) => {
          this.showError(err, "complaintsSystem.toasts.loadFailed");
        })
        .finally(() => {
          this.actionSaving = false;
        });
    },
    goToBooking(bookingId) {
      if (!bookingId) return;
      this.$router.push({ name: "Bookings", params: { id: bookingId } });
    },
    complainantName(item) {
      if (item.complainant && item.complainant.name) return item.complainant.name;
      if (item.complainant_name) return item.complainant_name;
      return "#" + item.complainant_id;
    },
    partyName(party) {
      if (!party) return "-";
      return party.name || (party.id ? "#" + party.id : "-");
    },
    actionAdminName(action) {
      if (action.admin_name) return action.admin_name;
      if (action.creator) {
        return `${action.creator.first_name || ""} ${action.creator.last_name || ""}`.trim() || "-";
      }
      return "-";
    },
    statusLabel(status) {
      const key = "complaintsSystem.statuses." + status;
      return this.$te(key) ? this.$t(key) : status;
    },
    statusColor(status) {
      switch (status) {
        case "NEW":
          return "red";
        case "IN_REVIEW":
          return "orange";
        case "RESOLVED":
          return "green";
        default:
          return "grey";
      }
    },
    typeLabel(type) {
      const key = "complaintsSystem.complainantTypes." + type;
      return this.$te(key) ? this.$t(key) : type || "-";
    },
    typeColor(type) {
      return type === "CAPTAIN" ? "indigo" : "teal";
    },
    categoryLabel(category) {
      const key = "complaintsSystem.categories." + category;
      return this.$te(key) ? this.$t(key) : String(category);
    },
    detailLabel(code) {
      const key = "complaintsSystem.details." + code;
      return this.$te(key) ? this.$t(key) : String(code);
    },
    actionTypeLabel(type) {
      const key = "complaintsSystem.actionTypes." + type;
      return this.$te(key) ? this.$t(key) : type;
    },
    // Final-version doc 2026-10-05: company clock (UTC+3), not the PC's zone.
    formatDate(date) {
      return formatCompanyTime(date, "MM-DD HH:mm");
    },
    formatDateTime(date) {
      return formatCompanyTime(date, "DD/MM/YYYY HH:mm");
    },
    formatSize(size) {
      if (size === null || size === undefined || size === "") return "";
      if (size >= 1048576) return (size / 1048576).toFixed(1) + " MB";
      if (size >= 1024) return Math.round(size / 1024) + " KB";
      return size + " B";
    },
    showError(err, fallbackKey) {
      const message =
        (err.response && err.response.data && err.response.data.message) || this.$t(fallbackKey);
      window.getApp.$emit("SHOW_TOAST", message, "red");
    },
  },
};
</script>

<style src="@/assets/finance-compact.css"></style>

<style scoped>
.complaint-row {
  cursor: pointer;
}

.booking-link {
  color: #1976d2;
  cursor: pointer;
  text-decoration: underline;
}

.booking-link:hover {
  color: #1565c0;
}

.party-card {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  padding: 8px 12px;
  height: 100%;
}

.party-title {
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.54);
}

.party-name {
  font-size: 14px;
  font-weight: 700;
  margin-top: 2px;
}

.party-phone {
  font-size: 13px;
  margin-top: 2px;
}

.detail-line {
  margin-top: 6px;
  font-size: 13px;
}

.section-title {
  margin-top: 14px;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 700;
}

.empty-note {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.54);
}

.attachments-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.attachment-item {
  max-width: 220px;
}

.attachment-media {
  max-width: 220px;
  max-height: 160px;
  border-radius: 4px;
  cursor: pointer;
  display: block;
}

.attachment-meta {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.54);
  margin-top: 2px;
}
</style>
